import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getFAQList, createFAQ, editFAQ, deleteFAQ, handleFAQStatus } from "../../Services/FAQ";
import { Success, Error, Confirmation } from "../../Components/Modal";
import Table from "../../Components/Table";
import StatusHandler from "../../Components/Table/StatusHandler";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { getSerialNumber } from "../../Utils/helpers";
import PageHeader from "../../Components/PageHeader";
import "../CMSManagement/CMSManagement.css";

const ROLES = [
    { value: "user", label: "User App", icon: "fa-user" },
    { value: "driver", label: "Driver App", icon: "fa-truck" },
];

const FAQManagement = () => {
    const queryClient = useQueryClient();
    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            question: "",
            answer: "",
            roles: ["user"],
            status: true,
        },
    });

    const activeRoles = watch("roles") || [];
    const statusActive = watch("status");

    const { data: faqData, isLoading, isFetching } = useQuery(
        ["faqList", page, perPage, searchTerm],
        () => getFAQList(page, perPage, searchTerm),
        { keepPreviousData: true }
    );

    const createMutation = useMutation(createFAQ, {
        onSuccess: (res) => {
            Success("FAQ Created", res.data?.message || "FAQ has been added successfully.");
            closeModal();
            queryClient.invalidateQueries("faqList");
        },
        onError: (err) => Error(err?.response?.data?.message || "Something went wrong"),
    });

    const editMutation = useMutation(editFAQ, {
        onSuccess: (res) => {
            Success("FAQ Updated", res.data?.message || "FAQ has been updated successfully.");
            closeModal();
            queryClient.invalidateQueries("faqList");
        },
        onError: (err) => Error(err?.response?.data?.message || "Something went wrong"),
    });

    const deleteMutation = useMutation(deleteFAQ, {
        onSuccess: () => {
            Success("FAQ Deleted", "The FAQ has been removed.");
            queryClient.invalidateQueries("faqList");
        },
        onError: (err) => Error(err?.response?.data?.message || "Something went wrong"),
    });

    const statusMutation = useMutation(handleFAQStatus, {
        onSuccess: (res) => {
            Success("Status Update", res.data?.message || "FAQ status updated");
            queryClient.invalidateQueries("faqList");
        },
        onError: (err) => Error(err?.response?.data?.message || "Something went wrong"),
    });

    const isSaving = createMutation.isLoading || editMutation.isLoading;

    const onSubmit = (data) => {
        const payload = {
            ...data,
            id: editData?._id,
            status: data.status ? 1 : 0,
        };
        if (editData) editMutation.mutate(payload);
        else createMutation.mutate(payload);
    };

    const openCreateModal = () => {
        setEditData(null);
        reset({ question: "", answer: "", roles: ["user"], status: true });
        setShowModal(true);
    };

    const handleEdit = (faq) => {
        setEditData(faq);
        reset({
            question: faq.question,
            answer: faq.answer,
            roles: faq.roles || ["user"],
            status: faq.status === 1 || faq.status === true || faq.status === "1",
        });
        setShowModal(true);
    };

    const handleDelete = (id) => {
        Confirmation("Are you sure you want to delete this FAQ?", "Yes", () => {
            deleteMutation.mutate(id);
        });
    };

    const toggleStatus = (item) => {
        Confirmation(
            `Are you sure you want to ${item.status === 1 || item.status === true ? "deactivate" : "activate"} this FAQ?`,
            "Yes",
            () => statusMutation.mutate(item._id)
        );
    };

    const closeModal = () => {
        setShowModal(false);
        setEditData(null);
        reset({ question: "", answer: "", roles: ["user"], status: true });
    };

    const toggleRole = (role) => {
        const current = [...activeRoles];
        if (current.includes(role)) {
            if (current.length > 1) setValue("roles", current.filter((r) => r !== role));
        } else {
            setValue("roles", [...current, role]);
        }
    };

    const isActiveStatus = (status) => status === 1 || status === true || status === "1";

    return (
        <div className="app-content content">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="configuration">
                        <div className="user-details-wrapper mt-0">
                            <PageHeader
                                title="FAQ Management"
                                subtitle="Manage frequently asked questions and help center content"
                                action={
                                    <button
                                        type="button"
                                        className="cms-add-btn d-inline-flex align-items-center"
                                        onClick={openCreateModal}
                                    >
                                        <i className="fas fa-plus me-2" />
                                        Add New FAQ
                                    </button>
                                }
                            />

                            <Table
                                tableTitles={["S.No", "Question", "Roles", "Status", "Actions"]}
                                data={faqData?.data?.data?.docs || []}
                                isLoading={isLoading}
                                isFetching={isFetching}
                                totalDocs={faqData?.data?.data?.totalDocs || 0}
                                totalPages={faqData?.data?.data?.totalPages || 0}
                                setPage={(nextPage) => setPage(nextPage)}
                                setSearchString={setSearchTerm}
                                perPage={perPage}
                                setPerPage={setPerPage}
                            >
                                {faqData?.data?.data?.docs?.map((row, index) => (
                                    <tr key={row._id}>
                                        <td>{getSerialNumber(faqData?.data?.data, index)}</td>
                                        <td>
                                            <span className="fw-bold">{row.question}</span>
                                        </td>
                                        <td>
                                            {row.roles?.map((role) => (
                                                <span
                                                    key={role}
                                                    className={`cms-role-badge ${role === "user" ? "cms-role-badge--user" : "cms-role-badge--driver"}`}
                                                >
                                                    {role}
                                                </span>
                                            ))}
                                        </td>
                                        <td>
                                            <span
                                                className={`status-pill ${isActiveStatus(row.status) ? "status-active" : "status-inactive"}`}
                                            >
                                                {isActiveStatus(row.status) ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td>
                                            <StatusHandler
                                                edit
                                                active
                                                item={{ ...row, active: isActiveStatus(row.status) }}
                                                onClick={() => toggleStatus(row)}
                                                onEdit={() => handleEdit(row)}
                                                onDelete={() => handleDelete(row._id)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </Table>
                        </div>
                    </section>
                </div>
            </div>

            <Modal
                show={showModal}
                onHide={closeModal}
                size="lg"
                centered
                className="cms-modal premium-cms-modal"
                scrollable
            >
                <Modal.Header closeButton className="cms-modal__header">
                    <div className="cms-modal__header-main">
                        <div className="cms-modal__header-icon">
                            <i className="fas fa-question-circle" />
                        </div>
                        <div>
                            <Modal.Title>{editData ? "Update FAQ" : "Add New FAQ"}</Modal.Title>
                            <p className="cms-modal__subtitle">
                                {editData
                                    ? "Update question, answer, and visibility settings"
                                    : "Create a help center entry for users and drivers"}
                            </p>
                            <span className="cms-modal__mode-badge">
                                {editData ? "Edit Mode" : "New Entry"}
                            </span>
                        </div>
                    </div>
                </Modal.Header>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body className="cms-modal__body">
                        <div className="cms-settings-panel">
                            <div className="cms-settings-panel__col">
                                <span className="cms-settings-label">Target Apps</span>
                                <div className="cms-app-pills">
                                    {ROLES.map((role) => {
                                        const isSelected = activeRoles.includes(role.value);
                                        return (
                                            <button
                                                key={role.value}
                                                type="button"
                                                className={`cms-app-pill ${isSelected ? "is-selected" : ""}`}
                                                onClick={() => toggleRole(role.value)}
                                            >
                                                <span className="cms-app-pill__inner">
                                                    <i className={`fas ${role.icon}`} />
                                                    {role.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="cms-settings-panel__divider" />

                            <div className="cms-settings-panel__col cms-settings-panel__col--status">
                                <span className="cms-settings-label">Publication</span>
                                <div className={`cms-status-row ${statusActive ? "is-active" : ""}`}>
                                    <div className="cms-status-row__text">
                                        <strong>{statusActive ? "Published" : "Draft"}</strong>
                                        <span>
                                            {statusActive
                                                ? "Visible in selected apps"
                                                : "Hidden from all apps"}
                                        </span>
                                    </div>
                                    <div className="cms-switch-wrap">
                                        <span className="cms-switch__label">{statusActive ? "ON" : "OFF"}</span>
                                        <label className="cms-switch">
                                            <input type="checkbox" {...register("status")} />
                                            <span className="cms-switch__slider" />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="cms-content-block">
                            <div className="cms-content-block__head">
                                <h5 className="cms-content-block__title">
                                    <i className="fas fa-comments" />
                                    FAQ Content
                                </h5>
                                <span className="cms-content-block__hint">Question &amp; answer pair</span>
                            </div>
                            <div className="cms-content-block__body">
                                <div>
                                    <label className="cms-field-label">
                                        Question <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`cms-input ${errors.question ? "is-invalid" : ""}`}
                                        placeholder="e.g. How do I cancel a booking?"
                                        {...register("question", { required: "Question is required" })}
                                    />
                                    {errors.question && (
                                        <span className="cms-field-error">{errors.question.message}</span>
                                    )}
                                </div>
                                <div>
                                    <label className="cms-field-label">
                                        Answer <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className={`cms-textarea ${errors.answer ? "is-invalid" : ""}`}
                                        placeholder="Write a clear, detailed answer for users..."
                                        rows={6}
                                        {...register("answer", { required: "Answer is required" })}
                                    />
                                    {errors.answer && (
                                        <span className="cms-field-error">{errors.answer.message}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Modal.Body>

                    <div className="cms-modal-footer">
                        <button type="button" className="cms-btn-cancel" onClick={closeModal}>
                            Cancel
                        </button>
                        <button type="submit" className="cms-btn-save" disabled={isSaving}>
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                                    Saving...
                                </>
                            ) : editData ? (
                                <>
                                    <i className="fas fa-check me-2" />
                                    Save Changes
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-plus me-2" />
                                    Create FAQ
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default FAQManagement;
